/**
 * The only control over audio on the site, and the only thing that may create an `AudioContext`.
 *
 * Two states are easy — on and off — and the third is the one that makes this component exist. A
 * visitor who turned sound on last week arrives with the preference stored and *no gesture yet*:
 * every browser refuses to start audio in that state, and a toggle that draws itself as "on" over
 * a silent page is a bug report. So a restored preference is shown as on but marked as waiting,
 * with a quiet "tap to enable sound" beside it, and the first pointer or key anywhere on the page
 * turns it into real sound.
 *
 * The hint sits inline in the header next to the button, which is also how it keeps out of the
 * way of the first-visit tip (F17): that tip owns a fixed slot at the bottom of the viewport, so
 * two elements that can never share a coordinate can never collide, and neither has to know the
 * other exists.
 */
import { useCallback, useEffect, useState } from 'react'
import { sound } from '@/audio/audio'

export function SoundToggle() {
  // Read from the board during the first render rather than in an effect. The preference is
  // external state that exists before this component does, and applying it in an effect would
  // paint "Sound off" for one frame on a page the visitor left unmuted — a flicker that reads as
  // the setting having been forgotten. `restore` only reads storage and sets a flag, so running
  // it again under StrictMode's double invocation changes nothing.
  const [on, setOn] = useState(() => sound.restore())
  const [waiting, setWaiting] = useState(() => sound.enabled && sound.ctx === null)

  useEffect(() => {
    if (!waiting) return

    // Restored as on: arm the page for the first gesture, whatever it is. `pointerdown` rather
    // than `click` so the audio starts on the same event the button uses, and `keydown` so a
    // keyboard-only visitor is not left in the waiting state forever.
    const wake = () => {
      sound.unlock()
      setWaiting(false)
    }
    window.addEventListener('pointerdown', wake, { once: true })
    window.addEventListener('keydown', wake, { once: true })
    return () => {
      window.removeEventListener('pointerdown', wake)
      window.removeEventListener('keydown', wake)
    }
  }, [waiting])

  const toggle = useCallback(() => {
    const next = !sound.enabled
    sound.setEnabled(next)
    // This handler *is* a user gesture, so turning sound on here can unlock immediately; there is
    // nothing left to wait for and the hint would be a lie.
    if (next) sound.unlock()
    setOn(next)
    setWaiting(false)
  }, [])

  return (
    <div className="flex items-center gap-2">
      {waiting ? (
        <span className="text-xs text-ink/60">tap to enable sound</span>
      ) : null}
      <button
        type="button"
        onClick={toggle}
        aria-pressed={on}
        // Named explicitly, because the word beside the icon is hidden on a narrow phone and a
        // button whose only accessible name is a visible label loses it along with the label.
        aria-label={on ? 'Sound on' : 'Sound off'}
        className="mha-soundbtn flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-ink/70 transition-colors hover:bg-ink/10 hover:text-ink"
      >
        <SpeakerIcon on={on} />
        <span className="mha-soundbtn__label">{on ? 'Sound on' : 'Sound off'}</span>
      </button>
    </div>
  )
}

/** Two states of one speaker: waves when on, a struck-through cone when off. */
function SpeakerIcon({ on }: { on: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      width="16"
      height="16"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 7.5h2.5L10 4.5v11L6.5 12.5H4z" />
      {on ? (
        <>
          <path d="M13 7.5a3.5 3.5 0 0 1 0 5" />
          <path d="M15.2 5.3a6.5 6.5 0 0 1 0 9.4" />
        </>
      ) : (
        <path d="M13 8l4 4m0-4l-4 4" />
      )}
    </svg>
  )
}
